'use client';

import { useState, useRef, useEffect, useCallback } from 'react';

import Image from 'next/image';
import { Box, Button, Stack, SxProps, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import { useAppContext } from '@/context';
import TAGLink from '@/components/TAGLink';
import { clickContext } from '@/utils';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { App, FacilityBrand, NavigationGroup } from '@/types/generated';
import LanguageToggle from '@/components/LanguageToggle/LanguageToggle';

import ContentTileCard from './ContentTileCard';
import { headerStyles } from './Header.styles';

const MEGA_MENU_CLOSE_DELAY_MS = 150;

interface HeaderDesktopProps {
	headerData?: App['header'] | null;
	logo?: App['logo'] | null;
	hideNavigation?: boolean;
	hideSchedule?: boolean;
	showLanguageToggle?: boolean;
	logoHref?: string;
	/** When set, overrides the Contentful phone number so a DNI snippet can swap it. */
	phoneSwapNumber?: string;
	/** TTB-322: when true, render "Schedule now" as a ghost (tertiary) button. */
	ghostPrimaryScheduleButton?: boolean;
}

const HeaderDesktop = ({
	headerData,
	logo,
	hideNavigation = false,
	hideSchedule = false,
	showLanguageToggle = false,
	logoHref = '/',
	phoneSwapNumber,
	ghostPrimaryScheduleButton = false,
}: HeaderDesktopProps) => {
	const { config } = useAppContext();

	const navMenuLinks = headerData?.navigationMenu?.headerMainNavLinksCollection;

	const phoneNumber = phoneSwapNumber ?? headerData?.phoneNumber?.linkText ?? headerData?.phoneNumber?.externalUrl;
	// The DNI/Patient Prism swap number arrives as raw E.164 (+13526190138).
	// Never show E.164 to the end user — format the swapped value to US
	// (xxx) xxx-xxxx. The DNI snippet swaps by matching digits, so the formatted
	// number is still swapped. The Contentful number is already display-formatted,
	// so it is rendered verbatim.
	const phoneDisplay = phoneSwapNumber
		? formatPhoneNumber(phoneSwapNumber, FacilityBrand.ClearChoice) ?? phoneSwapNumber
		: phoneNumber;
	const findCenterHref = headerData?.locationBarFindOfficeUrl ?? '/locations';
	const scheduleHref = headerData?.schedulingButtonUrl ?? '/schedule';

	// Use index as identifier — mirrors NavigationMenu.tsx key={index} pattern.
	const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	// Timer ref to delay closing the menu — gives user time to move mouse from nav tab to panel
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Close on Escape
	useEffect(() => {
		if (activeMenuIndex === null) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setActiveMenuIndex(null);
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [activeMenuIndex]);

	// Clear any pending close timer
	const cancelClose = useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);

	// Prevent pending delayed close callbacks from firing after component unmount
	useEffect(() => {
		return () => cancelClose();
	}, [cancelClose]);

	// Schedule closing after a brief delay so the user can move mouse from nav to panel
	const scheduleClose = useCallback(() => {
		cancelClose();
		closeTimerRef.current = setTimeout(() => setActiveMenuIndex(null), MEGA_MENU_CLOSE_DELAY_MS);
	}, [cancelClose]);

	const handleNavItemMouseEnter = useCallback(
		(index: number, hasSubmenu: boolean) => {
			cancelClose();
			if (hasSubmenu) setActiveMenuIndex(index);
		},
		[cancelClose]
	);

	const handleClose = useCallback(() => setActiveMenuIndex(null), []);

	// Helper: render a single navigation group (category header + sub-links, or standalone link)
	const renderGroup = (group: NavigationGroup) => {
		const groupTitle = group.groupLink?.linkText ?? '';
		const groupHref = group.groupLink?.pageData?.path ?? group.groupLink?.externalUrl;
		const subLinks = (group.linksCollection?.items ?? []).filter(Boolean);

		if (subLinks.length > 0) {
			return (
				<Box key={group.sys?.id} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{/* Category label — muted, book weight */}
					<Typography sx={headerStyles.megaMenuGroupTitle}>{groupTitle}</Typography>
					{subLinks.map((link) => (
						<TAGLink
							key={link?.sys?.id}
							href={link?.pageData?.path ?? link?.externalUrl ?? '#'}
							title={link?.linkText ?? ''}
							dataTestId={`mega_sub_${(link?.linkText ?? '').toLowerCase().replace(/\s+/g, '_')}`}
							noLinkStyle
							linkSx={headerStyles.linkBase}
						>
							<Typography sx={headerStyles.megaMenuSubLink} onClick={handleClose}>
								{link?.linkText}
							</Typography>
						</TAGLink>
					))}
				</Box>
			);
		}

		// Standalone group link (no sub-links)
		return (
			<TAGLink
				key={group.sys?.id}
				href={groupHref ?? '#'}
				title={groupTitle}
				dataTestId={`mega_link_${groupTitle.toLowerCase().replace(/\s+/g, '_')}`}
				noLinkStyle
				linkSx={headerStyles.linkBase}
			>
				<Typography sx={headerStyles.megaMenuGroupLink} onClick={handleClose}>
					{groupTitle}
				</Typography>
			</TAGLink>
		);
	};

	// Render a mega-menu for EACH nav item (always in DOM for SEO).
	// Positioned absolutely relative to desktopHeaderContainer — no portal, no JS measurement.
	const renderMegaMenuForIndex = (index: number) => {
		const item = navMenuLinks?.items?.[index];
		if (!item?.headerSubNavMenu) return null;

		const subNavMenu = item.headerSubNavMenu;
		const groups = (subNavMenu?.headerSubNavGroupsCollection?.items ?? []).filter(Boolean) as NavigationGroup[];
		const tiles = (subNavMenu?.contentTilesCollection?.items ?? []).filter(Boolean);
		const hasSubmenu = groups.length > 0;

		if (!hasSubmenu) return null;

		// Split groups evenly across 2 columns
		const half = Math.ceil(groups.length / 2);
		const col1 = groups.slice(0, half);
		const col2 = groups.slice(half);

		return (
			<Box
				key={`mega-menu-${index}`}
				sx={[
					headerStyles.megaMenuWrapper as object,
					{
						visibility: activeMenuIndex === index ? 'visible' : 'hidden',
						opacity: activeMenuIndex === index ? 1 : 0,
						pointerEvents: activeMenuIndex === index ? 'auto' : 'none',
						transition: 'opacity 0.3s ease-out',
					},
				]}
				data-testid={`desktop_mega_menu_${index}`}
				onMouseEnter={cancelClose}
				onMouseLeave={scheduleClose}
			>
				<Box sx={headerStyles.megaMenuPanel}>
					{/* Row of 3 columns: link col1, link col2, tiles */}
					<Box sx={headerStyles.megaMenuColumnsContainer}>
						{/* ── Column 1: first half of nav groups ── */}
						<Box sx={headerStyles.megaMenuColumn} data-testid={`mega_menu_col1_${index}`}>
							{col1.map((group) => renderGroup(group))}
						</Box>

						{/* ── Column 2: second half of nav groups ── */}
						<Box sx={headerStyles.megaMenuColumn} data-testid={`mega_menu_col2_${index}`}>
							{col2.map((group) => renderGroup(group))}
						</Box>

						{/* ── Column 3: content tile cards ── */}
						{tiles.length > 0 && (
							<Box sx={headerStyles.megaMenuTilesSection} data-testid={`mega_menu_tiles_${index}`}>
								{tiles.map((tile) => {
									if (!tile) return null;
									return <ContentTileCard key={tile.sys?.id} tile={tile} onClick={handleClose} />;
								})}
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		);
	};

	// Render mega-menus for all nav items (always in DOM for SEO)
	const megaMenus = navMenuLinks?.items?.map((_, index) => renderMegaMenuForIndex(index)) ?? [];

	return (
		<Box ref={headerRef} sx={{ width: '100%' }}>
			{/* ─── Row 1: Logo + Utility actions ─── */}
			<Box sx={headerStyles.desktopTopRow}>
				{logo?.url &&
					(hideNavigation ? (
						<Box
							data-testid="header_logo_clearchoice2_desktop"
							sx={{
								...headerStyles.linkBase,
								display: 'block',
								lineHeight: 0,
								flexShrink: 0,
								cursor: 'default',
							}}
						>
							<Box sx={headerStyles.desktopLogo}>
								<Image
									src={logo.url}
									alt={logo.description || 'ClearChoice'}
									width={164}
									height={48}
									style={{ height: '48px', width: '164px', objectFit: 'contain' }}
									priority
								/>
							</Box>
						</Box>
					) : (
						<TAGLink
							href={logoHref}
							title="ClearChoice"
							analyticsContext={clickContext({ brand: config.name, type: 'logoClick' })}
							dataTestId="header_logo_clearchoice2_desktop"
							linkSx={{ ...headerStyles.linkBase, display: 'block', lineHeight: 0, flexShrink: 0 }}
						>
							<Box sx={headerStyles.desktopLogo}>
								<Image
									src={logo.url}
									alt={logo.description || 'ClearChoice'}
									width={164}
									height={48}
									style={{ height: '48px', width: '164px', objectFit: 'contain' }}
									priority
								/>
							</Box>
						</TAGLink>
					))}

				{!hideNavigation && (
					<Stack direction="row" alignItems="center" sx={headerStyles.desktopActions}>
						{/* Find a center */}
						<TAGLink
							href={findCenterHref}
							title="Find a center"
							analyticsContext={clickContext({ brand: config.name, type: 'findCenter' })}
							dataTestId="header_find_center"
							linkSx={headerStyles.linkBase}
						>
							<Stack direction="row" alignItems="center" sx={headerStyles.desktopActionLink}>
								<LocationOnOutlinedIcon sx={headerStyles.desktopUtilityIcon} />
								<Typography sx={headerStyles.desktopActionLinkText}>Find a center</Typography>
							</Stack>
						</TAGLink>

						{/* Phone — display only, not a link */}
						{phoneNumber && (
							<Stack
								direction="row"
								alignItems="center"
								sx={headerStyles.desktopPhonePill}
								data-testid="header_phone_number"
							>
								<PhoneOutlinedIcon
									sx={[headerStyles.desktopUtilityIcon, { color: 'inherit' }] as SxProps<Theme>}
								/>
								<Typography sx={headerStyles.desktopPhonePillText}>{phoneDisplay}</Typography>
							</Stack>
						)}

						{/* Schedule now */}
						{!hideSchedule && (
							<Button
								component="a"
								href={scheduleHref}
								data-testid="header_schedule_button"
								variant={ghostPrimaryScheduleButton ? 'tertiaryDefault' : undefined}
								sx={
									(ghostPrimaryScheduleButton
										? headerStyles.scheduleButtonGhost
										: headerStyles.scheduleButton) as SxProps
								}
								disableRipple
							>
								Schedule now
							</Button>
						)}

						{/* Language selector */}
						{showLanguageToggle && <LanguageToggle isLargeDevice={true} />}
					</Stack>
				)}
			</Box>

			{/* ─── Row 2: Nav tabs ─── */}
			{/* Mirrors NavigationMenu.tsx: iterate navMenuLinks?.items, guard item && item.headerMainNavLink */}
			{!hideNavigation && (
				<>
					<Box sx={headerStyles.desktopBottomRow}>
						{navMenuLinks?.items?.map((item, index) => {
							if (!item || !item.headerMainNavLink) return null;

							const { headerMainNavLink, headerSubNavMenu } = item;
							const title = headerMainNavLink.linkText ?? '';
							const href = headerMainNavLink.pageData?.path ?? headerMainNavLink.externalUrl ?? '#';
							const hasSubmenu = (headerSubNavMenu?.headerSubNavGroupsCollection?.items ?? []).some(
								Boolean
							);
							const isActive = activeMenuIndex === index;

							if (!hasSubmenu) {
								// Plain nav item — direct link, no mega menu
								return (
									<Box
										key={index}
										sx={headerStyles.navItem}
										onMouseEnter={() => handleNavItemMouseEnter(index, false)}
										onMouseLeave={scheduleClose}
									>
										<Box component="span" sx={headerStyles.navItemInner}>
											<TAGLink
												href={href}
												title={title}
												noLinkStyle
												dataTestId={`nav_tab_${title.toLowerCase().replace(/\s+/g, '_')}`}
												linkSx={headerStyles.linkBase}
											>
												{title}
											</TAGLink>
										</Box>
									</Box>
								);
							}

							// Nav item with submenu — hover opens mega menu
							return (
								<Box
									key={index}
									component="button"
									aria-expanded={isActive}
									aria-haspopup="true"
									onMouseEnter={() => handleNavItemMouseEnter(index, true)}
									onMouseLeave={scheduleClose}
									sx={[headerStyles.navItem, headerStyles.navItemButton] as SxProps<Theme>}
									data-testid={`nav_tab_${title.toLowerCase().replace(/\s+/g, '_')}`}
								>
									<Box
										component="span"
										sx={
											[
												headerStyles.navItemInner,
												isActive && headerStyles.navItemActive,
											] as SxProps<Theme>
										}
									>
										{title}
										<ExpandMoreIcon
											sx={{
												...headerStyles.desktopUtilityIcon,
												transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
											}}
										/>
									</Box>
								</Box>
							);
						})}
					</Box>

					{/* ─── Mega-menus: absolute children of the header content box ─── */}
					{/* position: absolute on each wrapper is relative to desktopHeaderContainer  */}
					{/* (the nearest positioned ancestor), so width always matches the pill.    */}
					{megaMenus}
				</>
			)}
		</Box>
	);
};

export default HeaderDesktop;
